import { formattedCurrency, withStyles, AccountBalanceResponse, AccountLimitsResponse } from 'vopay';
import { FunctionComponent, useRef, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavMenu } from '@/providers/NavMenu.provider';
import LottieFile from '../../components/LottieFile';
import LottiePayment from '../../LottieFiles/Payments.json';
import ExpandCard from '../../components/ExpandCard';

import type { LottieRefCurrentProps } from 'lottie-react';

import styles from './InteractiveInfo.module.scss';

import { Title } from 'UI';

const cn = withStyles(styles);

type TotalPaymentsCardProps = {
  accountBalanceResponse: AccountBalanceResponse;
  accountLimitsResponse: AccountLimitsResponse;
};

type FC = FunctionComponent<TotalPaymentsCardProps>;

const TotalPaymentsCard: FC = ({ accountBalanceResponse, accountLimitsResponse }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp' });
  const { onSubNavClick } = useNavMenu();
  const paymentRef = useRef<LottieRefCurrentProps>(null);

  const handleLottieFileHover = (
    ref: RefObject<LottieRefCurrentProps | null>,
    isEnter: boolean,
    frameRange: [number, number] = [0, 45],
  ) => {
    if (!ref.current) return;

    ref.current.pause();
    const [start, end] = frameRange;

    if (isEnter) {
      ref.current.setDirection(1);
      ref.current.playSegments([start, end], true);
    } else {
      ref.current.setDirection(-1);
      ref.current.playSegments([end, start], true);
    }
  };

  const parseAmount = (value: string | undefined | null): number => {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  return (
    <ExpandCard
      align="center"
      buttonText={t('Widgets.InteractiveInfo.openSinglePayment')}
      onMouseOver={() => handleLottieFileHover(paymentRef, true)}
      onMouseOut={() => handleLottieFileHover(paymentRef, false)}
      onNavigateClick={() => onSubNavClick('singlePayment', '/single-payment', t('payment'))}
      top={
        <div className={cn('totalBalanceTopContainer')}>
          <div className={cn('totalBalanceTop')}>
            <Title level="6" emphasized={false} className={cn('totalCardLabel')}>
              {t('Widgets.InteractiveInfo.totalMonthlyPayments')}
            </Title>
            <div className={cn('totalCardCurrency')}>
              <Title level="5" emphasized={false}>
                {accountBalanceResponse?.Currency}
              </Title>
              <Title level="3" className={cn('totalCardAmount')}>
                {formattedCurrency(
                  String(
                    parseAmount(accountLimitsResponse?.Outbound?.Limits?.Monthly ?? 0) -
                      parseAmount(accountLimitsResponse?.Outbound?.RemainingLimits?.Monthly ?? 0),
                  ),
                )}
              </Title>
            </div>
          </div>
          <LottieFile animationData={LottiePayment} lottieRef={paymentRef} />
        </div>
      }
      contents={
        <div className={cn('totalBalanceContents')}>
          <div className={cn('totalBalanceCard')}>
            <div>{t('Widgets.InteractiveInfo.dailyPayments')}</div>
            <div>
              {accountBalanceResponse?.Currency}{' '}
              {formattedCurrency(
                String(
                  parseAmount(accountLimitsResponse?.Outbound?.Limits?.Daily ?? 0) -
                    parseAmount(accountLimitsResponse?.Outbound?.RemainingLimits?.Daily ?? 0),
                ),
              )}
            </div>
          </div>
          <div className={cn('totalBalanceCard')}>
            <div>{t('Widgets.InteractiveInfo.weeklyPayments')}</div>
            <div>
              {accountBalanceResponse?.Currency}{' '}
              {formattedCurrency(
                String(
                  parseAmount(accountLimitsResponse?.Outbound?.Limits?.Weekly ?? 0) -
                    parseAmount(accountLimitsResponse?.Outbound?.RemainingLimits?.Weekly ?? 0),
                ),
              )}
            </div>
          </div>
          <div className={cn('totalBalanceCard')}>
            <div>{t('Widgets.InteractiveInfo.monthlyPayments')}</div>
            <div>
              {accountBalanceResponse?.Currency}{' '}
              {formattedCurrency(
                String(
                  parseAmount(accountLimitsResponse?.Outbound?.Limits?.Monthly ?? 0) -
                    parseAmount(accountLimitsResponse?.Outbound?.RemainingLimits?.Monthly ?? 0),
                ),
              )}
            </div>
          </div>
        </div>
      }
    />
  );
};

export default TotalPaymentsCard;
